/**
 * AI-extracted clause database for the Pillar Industrial Supply procurement demo.
 *
 * Self-contained: no runtime imports. Every `quote` is a verbatim substring of
 * one paragraph of the cited section in `contract-documents.ts`, and
 * `clauses.test.ts` fails if that stops being true. Clause ids are
 * `${contractId}:${section}`, so there is at most one clause per section.
 *
 * `rule` is what the invoice-compliance engine evaluates; `enforced` clauses
 * are checked on every invoice for the contract.
 */

export type ClauseKind =
  | 'payment_terms'
  | 'pricing'
  | 'price_adjustment'
  | 'rebate'
  | 'early_pay_discount'
  | 'incoterms'
  | 'duties_taxes'
  | 'lead_time'
  | 'late_delivery'
  | 'quality'
  | 'change_notification'
  | 'sla'
  | 'support'
  | 'service_credits'
  | 'chronic_failure'
  | 'liability_cap'
  | 'auto_renewal'
  | 'termination'
  | 'rate_card'
  | 'not_to_exceed'
  | 'expenses'
  | 'fuel_surcharge'
  | 'accessorials'
  | 'data_protection'
  | 'ai_data_use'
  | 'security'
  | 'insurance'
  | 'governing_law'
  | 'confidentiality'
  | 'safety_stock'
  | 'committed_spend'
  | 'markup';

export type ClauseRule =
  | {
      kind: 'payment_terms';
      netDays: number;
      discount?: { pct: number; days: number };
    }
  | { kind: 'unit_price'; price: number; unit: string; currency: 'USD' }
  | { kind: 'freight_included'; incoterm: 'DAP' | 'DDP' | 'FCA' }
  | { kind: 'duties_included' }
  | { kind: 'max_pct'; appliesTo: string; maxPct: number }
  | { kind: 'not_to_exceed'; amount: number }
  | { kind: 'rate_card'; rates: { role: string; rate: number }[] }
  | {
      kind: 'sla_credit';
      target: number;
      tiers: { below: number; creditPct: number }[];
    }
  | { kind: 'rebate'; pct: number; threshold: number }
  | { kind: 'requires_proof'; charge: string }
  | { kind: 'notice'; days: number };

export type ClauseRisk = 'favorable' | 'standard' | 'unfavorable';

export interface Clause {
  /** `${contractId}:${section}`, e.g. 'SA-CORV-2024-017:4.2' */
  id: string;
  contractId: string;
  kind: ClauseKind;
  /** 'Payment terms' */
  label: string;
  /** Short normalized value: 'Net 45' */
  value: string;
  /** One plain-English sentence */
  detail: string;
  /** '4.2' or 'B.3' */
  section: string;
  /** Verbatim substring of one paragraph of that section */
  quote: string;
  /** 0.90 to 0.99 */
  confidence: number;
  /** Checked on every invoice */
  enforced: boolean;
  rule?: ClauseRule;
  risk?: ClauseRisk;
  riskNote?: string;
}

type ClauseInput = Omit<Clause, 'id'>;

function clause(input: ClauseInput): Clause {
  return { ...input, id: `${input.contractId}:${input.section}` };
}

const NET_15: ClauseRule = { kind: 'payment_terms', netDays: 15 };
const NET_45: ClauseRule = { kind: 'payment_terms', netDays: 45 };
const NET_60: ClauseRule = { kind: 'payment_terms', netDays: 60 };

export const CLAUSES: Clause[] = [
  // ---------------------------------------------------------------------------
  // MSA-GEN-2025-001
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: '4.1',
    kind: 'pricing',
    label: 'Platform fees',
    value: '$360,000/yr ($30,000/mo)',
    detail:
      'The platform subscription is $360,000 per contract year, invoiced as twelve $30,000 monthly installments.',
    quote:
      'a platform subscription fee of Three Hundred Sixty Thousand Dollars ($360,000) per Contract Year',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'unit_price', price: 30000, unit: 'month', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: '4.2',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail:
      'Pillar pays each undisputed invoice within 45 days after receiving a valid invoice.',
    quote:
      'Customer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'favorable',
    riskNote: 'Longer than the Net 30 that many software vendors ask for.',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: '4.4',
    kind: 'price_adjustment',
    label: 'Renewal price increase cap',
    value: 'Max +5% on renewal',
    detail:
      'Gently AI can raise the platform fee for a renewal term by at most 5%, and only with 120 days of notice.',
    quote:
      'any such increase shall not exceed five percent (5%) of the Platform Fees in effect during the immediately preceding twelve (12) months',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'max_pct', appliesTo: 'renewal price increase', maxPct: 5 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: '9.1',
    kind: 'liability_cap',
    label: 'Liability cap',
    value: '12 months of fees',
    detail:
      "Each party's total liability is capped at the fees paid or payable in the 12 months before the claim.",
    quote:
      'shall not exceed the Fees paid or payable by Customer under this Agreement during the twelve (12) months immediately preceding the event giving rise to the claim',
    confidence: 0.97,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: '9.3',
    kind: 'liability_cap',
    label: 'Liability carve-outs: data breach',
    value: 'Data breach: 3x fees',
    detail:
      "Indemnities, confidentiality breaches and willful misconduct are uncapped, and Gently AI's liability for a data breach is capped separately at three times the annual fees.",
    quote:
      'shall not exceed three (3) times the Fees paid or payable by Customer during the twelve (12) months immediately preceding the Security Incident',
    confidence: 0.96,
    enforced: false,
    risk: 'favorable',
    riskNote:
      'A super-cap for data incidents is stronger than the usual 12 months of fees.',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: '10.1',
    kind: 'insurance',
    label: 'Cyber insurance',
    value: '$5M cyber, $5M professional liability',
    detail:
      'Gently AI must carry $2M general liability, $5M technology professional liability and $5M cyber insurance during the term and for two years after.',
    quote:
      'cyber and network security liability insurance of $5,000,000 per claim',
    confidence: 0.95,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: '11.1',
    kind: 'auto_renewal',
    label: 'Term and auto-renewal',
    value: '24 months, then 12-month renewals; 60-day notice',
    detail:
      'The initial term ends October 31, 2027 and then renews for successive 12-month terms unless either party gives 60 days of notice.',
    quote:
      'unless either party gives written notice of non-renewal at least sixty (60) days before the end of the then-current Initial Term or Renewal Term',
    confidence: 0.98,
    enforced: false,
    rule: { kind: 'notice', days: 60 },
    risk: 'standard',
    riskNote:
      'Notice of non-renewal must be given by September 1, 2027 to avoid the first renewal term.',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: '11.2',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Customer, 90 days notice',
    detail:
      'Pillar can end the agreement or any statement of work at any time on 90 days of notice, with no termination fee.',
    quote:
      'Customer may terminate this Agreement or any SOW for convenience at any time on ninety (90) days’ prior written notice to Gently',
    confidence: 0.98,
    enforced: false,
    rule: { kind: 'notice', days: 90 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: '12.1',
    kind: 'security',
    label: 'SOC 2 Type II report',
    value: 'Annual SOC 2 Type II',
    detail:
      'Gently AI must give Pillar its SOC 2 Type II report every year, within 30 days after it is issued.',
    quote:
      'shall provide Customer with a copy of its SOC 2 Type II report annually, within thirty (30) days after the report is issued',
    confidence: 0.97,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: '12.4',
    kind: 'ai_data_use',
    label: 'No training on customer data',
    value: 'Not used to train models',
    detail:
      'Gently AI and its model providers may not use Pillar data or outputs to train or improve any AI model.',
    quote:
      'shall not use Customer Data or Outputs, or any content derived from them, to train, fine-tune or otherwise improve any machine learning or artificial intelligence model',
    confidence: 0.99,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: '14.1',
    kind: 'governing_law',
    label: 'Governing law',
    value: 'New York',
    detail:
      'New York law governs, and disputes go to the state and federal courts in New York County.',
    quote:
      'is governed by the laws of the State of New York, without regard to its conflict of laws principles',
    confidence: 0.99,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: 'A.7',
    kind: 'pricing',
    label: 'Implementation fee (statement of work 1)',
    value: '$180,000 fixed',
    detail:
      'Statement of work 1 is a $180,000 fixed fee, billed on acceptance of four milestones: $27,000, $63,000, $54,000 and $36,000.',
    quote:
      'The fee for SOW #1 is a fixed fee of One Hundred Eighty Thousand Dollars ($180,000)',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'not_to_exceed', amount: 180000 },
    risk: 'favorable',
    riskNote:
      'Fixed fee includes all expenses and is payable only on milestone acceptance.',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: 'B.1',
    kind: 'sla',
    label: 'Uptime commitment',
    value: '99.9% monthly uptime',
    detail:
      'The production platform must be available at least 99.9% of each month, excluding up to 4 hours of scheduled maintenance announced 72 hours ahead.',
    quote:
      'Gently shall make the Production Platform available with a Monthly Uptime Percentage of at least 99.9% in each calendar month',
    confidence: 0.99,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: 'B.2',
    kind: 'support',
    label: 'Support response times',
    value:
      'Priority 1: 1 hour any time; 2: 4 business hours; 3: 1 business day',
    detail:
      'Priority 1 issues get a response within 1 hour at any time with updates every 2 hours, Priority 2 within 4 business hours and Priority 3 within 1 business day.',
    quote:
      'Gently shall respond within one (1) hour, twenty-four (24) hours a day, seven (7) days a week, and shall provide a status update at least every two (2) hours until the issue is resolved or a workaround is in place',
    confidence: 0.98,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: 'B.3',
    kind: 'service_credits',
    label: 'Fee credits for missed uptime',
    value: '5% / 10% / 25% of monthly fee back; max 25%',
    detail:
      'Gently AI credits back 5% of the monthly fee below 99.9% uptime, 10% below 99.5% and 25% below 99.0%, capped at 25%, and must be requested within 30 days.',
    quote:
      'a service credit equal to the following percentage of the Monthly Fee for that month: (a) five percent (5%) of the Monthly Fee if the Monthly Uptime Percentage is below 99.9% but at least 99.5%; (b) ten percent (10%) of the Monthly Fee if it is below 99.5% but at least 99.0%; and (c) twenty-five percent (25%) of the Monthly Fee if it is below 99.0%',
    confidence: 0.97,
    enforced: true,
    rule: {
      kind: 'sla_credit',
      target: 99.9,
      tiers: [
        { below: 99.9, creditPct: 5 },
        { below: 99.5, creditPct: 10 },
        { below: 99.0, creditPct: 25 },
      ],
    },
    risk: 'standard',
    riskNote:
      'Credits are lost unless requested within 30 days after the month ends.',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: 'B.4',
    kind: 'chronic_failure',
    label: 'Repeated uptime failures',
    value: 'Below 99.0% in 3 of any 6 months: Pillar can exit',
    detail:
      'If uptime is below 99.0% in any three months of a rolling six-month period, Pillar may terminate without penalty.',
    quote:
      'If the Monthly Uptime Percentage is below 99.0% in any three (3) calendar months of any rolling six (6) month period, Customer may terminate the Agreement and any SOW without penalty',
    confidence: 0.98,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: 'B.5',
    kind: 'sla',
    label: 'Disaster recovery',
    value: 'At most 1 hour of data lost; service back in 4 hours',
    detail:
      'Gently AI’s recovery plan must lose at most 1 hour of data and restore service within 4 hours, and be tested every year.',
    quote:
      'recovery point objective (RPO) of one (1) hour and a recovery time objective (RTO) of four (4) hours',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: 'C.2',
    kind: 'data_protection',
    label: 'Data residency',
    value: 'US only',
    detail:
      'Customer data, including backups, is stored and processed only in data centers in the United States.',
    quote: 'exclusively in data centers located in the United States',
    confidence: 0.98,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: 'C.3',
    kind: 'data_protection',
    label: 'Notice of new data subcontractors',
    value: '30 days notice',
    detail:
      'Gently AI must give 30 days of notice before adding a subcontractor that handles Pillar data, and Pillar can object or end the affected services.',
    quote:
      'Gently shall give Customer at least thirty (30) days’ prior written notice of any new or replacement sub-processor',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'notice', days: 30 },
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-GEN-2025-001',
    section: 'C.5',
    kind: 'security',
    label: 'Breach notification',
    value: 'Within 48 hours',
    detail:
      'Gently AI must notify Pillar of a security incident within 48 hours after becoming aware of it.',
    quote:
      'Gently shall notify Customer without undue delay, and in any event within forty-eight (48) hours after becoming aware of a Security Incident',
    confidence: 0.98,
    enforced: false,
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // SA-CORV-2024-017
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '2.1',
    kind: 'auto_renewal',
    label: 'Term and renewal',
    value: 'Expires 2026-12-31, no auto-renewal',
    detail:
      'The agreement ends on December 31, 2026 and does not renew automatically, so a renewal decision is needed.',
    quote: 'continues until December 31, 2026',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
    riskNote:
      'Pillar must tell Corvane its renewal decision at least 90 days before expiry (Section 2.2).',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '3.1',
    kind: 'pricing',
    label: 'Base price',
    value: '$412.00 per 100 ft, fixed through 2025',
    detail:
      'The price is $412.00 per 100 ft of copper building wire and cannot increase before January 1, 2026.',
    quote:
      'The Price is fixed and will not be increased for any reason through December 31, 2025',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '3.3',
    kind: 'price_adjustment',
    label: 'Annual price adjustment',
    value: 'Follows copper price, max +3%/yr: $424.36 per 100 ft in 2026',
    detail:
      'The price moves with the market price of copper but can rise at most 3% per year, so the most Corvane can charge in 2026 is $424.36 per 100 ft.',
    quote:
      'The maximum Price that Supplier may charge for the Contract Year beginning January 1, 2026 is therefore $424.36 per 100 feet, being $412.00 increased by three percent (3%)',
    confidence: 0.97,
    enforced: true,
    rule: { kind: 'unit_price', price: 424.36, unit: 'C', currency: 'USD' },
    risk: 'standard',
    riskNote:
      'Any invoice line above $424.36 per 100 ft can be rejected or paid at the contract price.',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '3.4',
    kind: 'pricing',
    label: 'Most favored customer',
    value: 'No worse than any other customer',
    detail:
      'Corvane represents the price is no less favorable than any other customer pays for similar volumes, and must extend any lower price it offers.',
    quote:
      'Supplier represents that the Price is no less favorable than the price it charges any other customer for substantially similar product in comparable volumes and on comparable terms',
    confidence: 0.95,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '3.5',
    kind: 'rebate',
    label: 'Volume rebate',
    value: '2% above $25M/yr, credited quarterly',
    detail:
      'Corvane credits 2% of annual purchases above $25 million, quarterly in arrears.',
    quote:
      'Supplier shall pay Buyer a volume rebate equal to two percent (2%) of Net Purchases in each Contract Year in excess of Twenty-Five Million Dollars ($25,000,000)',
    confidence: 0.97,
    enforced: true,
    rule: { kind: 'rebate', pct: 2, threshold: 25000000 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '4.2',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail:
      'Pillar pays within 45 days after receiving a valid invoice, and any shorter term printed on an invoice has no effect.',
    quote:
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '5.1',
    kind: 'incoterms',
    label: 'Delivery terms',
    value: 'Delivered, freight included: Dallas DC / Phoenix DC',
    detail:
      'Corvane delivers to the Dallas DC or the Phoenix DC with freight included, and bears freight, insurance and loading up to that point.',
    quote:
      'Supplier shall deliver all Product DAP (Delivered at Place, Incoterms 2020) to Buyer’s distribution center in Dallas, Texas',
    confidence: 0.99,
    enforced: true,
    rule: { kind: 'freight_included', incoterm: 'DAP' },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '5.2',
    kind: 'pricing',
    label: 'No separate freight charges',
    value: 'Freight included in price',
    detail:
      'No freight, insurance, handling, fuel, reel-handling or other delivery charge may be billed on top of the price.',
    quote:
      'No freight, insurance, handling, fuel, reel-handling or other delivery-related charge is payable by Buyer in addition to the Price',
    confidence: 0.98,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '6.2',
    kind: 'lead_time',
    label: 'Lead time',
    value: '8 weeks from PO',
    detail: 'Corvane must deliver within 8 weeks of the purchase order date.',
    quote:
      'Supplier shall deliver the Product to the Delivery Point within eight (8) weeks after the date of Buyer’s purchase order',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '6.5',
    kind: 'late_delivery',
    label: 'Late delivery damages',
    value: '1.5% per week, max 10%',
    detail:
      'Corvane owes 1.5% of the late line value for each week of delay, up to 10% of that line.',
    quote:
      'Supplier shall pay Buyer liquidated damages equal to one and one-half percent (1.5%) of the value of the late line for each week or part of a week by which delivery is late, up to a maximum of ten percent (10%) of the value of that line',
    confidence: 0.97,
    enforced: false,
    risk: 'favorable',
    riskNote:
      'On time, in full over the last 12 months is 91.0%, so late-delivery damages are likely already accruing.',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '8.1',
    kind: 'quality',
    label: 'Quality standard',
    value:
      'Max 500 defects per million, factory test report with every reel lot',
    detail:
      'Each reel lot must arrive with 500 defects per million or fewer and ship with a factory test report.',
    quote:
      'Each Lot delivered shall have an incoming defect rate not exceeding five hundred (500) parts per million (PPM), measured in accordance with the Quality Agreement, and each shipment shall be accompanied by a mill test report for every reel lot',
    confidence: 0.98,
    enforced: false,
    risk: 'favorable',
    riskNote:
      'Defects on arrival over the last 12 months were 820 per million, above this limit.',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '8.4',
    kind: 'change_notification',
    label: 'Change notification',
    value: '180 days prior notice',
    detail:
      'Corvane must give 180 days of written notice before changing its plant, process or copper-rod source, and may not change until Pillar approves.',
    quote:
      'Supplier shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in the manufacturing plant, manufacturing process or source of copper rod or any other raw material used to make the Product',
    confidence: 0.98,
    enforced: false,
    rule: { kind: 'notice', days: 180 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '11.3',
    kind: 'insurance',
    label: 'Insurance',
    value: '$10M product liability',
    detail:
      'Corvane must carry $10 million product liability, $5 million general liability and $5 million recall insurance.',
    quote:
      'product liability insurance of not less than $10,000,000 per occurrence',
    confidence: 0.95,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '12.2',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Buyer, 180 days notice',
    detail:
      'Pillar can terminate for convenience on 180 days of notice; Corvane cannot.',
    quote:
      'Buyer may terminate this Agreement for convenience, in whole or in respect of any Product, on one hundred eighty (180) days’ prior written notice to Supplier',
    confidence: 0.98,
    enforced: false,
    rule: { kind: 'notice', days: 180 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-CORV-2024-017',
    section: '13.1',
    kind: 'governing_law',
    label: 'Governing law',
    value: 'New York',
    detail:
      'New York law governs, with exclusive jurisdiction in New York County courts.',
    quote:
      'This Agreement is governed by the laws of the State of New York, without regard to its conflict of laws principles',
    confidence: 0.99,
    enforced: false,
    risk: 'standard',
  }),

  // ---------------------------------------------------------------------------
  // QA-VITX-2023-004
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'QA-VITX-2023-004',
    section: '3.3',
    kind: 'quality',
    label: 'Certification and listing status',
    value: 'ISO 9001 and UL certification',
    detail:
      'Vitrex must keep ISO 9001 certification and a current UL listing, with every factory that makes the breakers on UL’s list of certified factories.',
    quote: 'certification of its quality management system to ISO 9001',
    confidence: 0.96,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'QA-VITX-2023-004',
    section: '6.3',
    kind: 'quality',
    label: 'Failed test results',
    value: 'Notify within 24 hours',
    detail:
      'Vitrex must notify Pillar within 24 hours of an out-of-specification result on a lot that has or may have shipped.',
    quote: 'Supplier shall notify Buyer within twenty-four (24) hours',
    confidence: 0.96,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'QA-VITX-2023-004',
    section: '7.2',
    kind: 'change_notification',
    label: 'Change notice and approval',
    value: '180 days notice + Pillar approval',
    detail:
      'Vitrex needs 180 days of prior written notice and written approval from Pillar before changing manufacturing site, subcontractor or component source, and may ship only from factories UL has certified.',
    quote:
      'without first giving Buyer at least one hundred eighty (180) days’ prior written notice and obtaining Buyer’s written approval',
    confidence: 0.99,
    enforced: false,
    rule: { kind: 'notice', days: 180 },
    risk: 'favorable',
    riskNote:
      'Shipping from a factory UL has not certified is a breach of this clause.',
  }),
  clause({
    contractId: 'QA-VITX-2023-004',
    section: '7.3',
    kind: 'change_notification',
    label: 'Other process changes',
    value: '90 days notice',
    detail:
      'Other changes to process, equipment, test methods or packaging need 90 days of notice and confirmation from Pillar.',
    quote:
      'Supplier shall give Buyer at least ninety (90) days’ prior written notice of any other change in the manufacturing process',
    confidence: 0.95,
    enforced: false,
    rule: { kind: 'notice', days: 90 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'QA-VITX-2023-004',
    section: '7.5',
    kind: 'quality',
    label: 'Rejection of affected lots',
    value: 'Reject lots and recover costs',
    detail:
      'If a critical change is made without approval, Pillar can reject the affected lots and recover scrap, control panel shop downtime, recall and re-certification costs.',
    quote:
      'Supplier shall promptly replace or credit each Affected Lot and reimburse Buyer for all costs that result',
    confidence: 0.97,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'QA-VITX-2023-004',
    section: '8.1',
    kind: 'quality',
    label: 'Audit rights',
    value: 'Yearly, or 48 hours notice after a problem',
    detail:
      'Pillar may audit once a year and on 48 hours of notice after defective product ships or an unapproved change is suspected.',
    quote:
      'once in each calendar year on at least ten (10) business days’ notice, and at any time on forty-eight (48) hours’ notice following a Quality Escape or a suspected unapproved change',
    confidence: 0.95,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'QA-VITX-2023-004',
    section: '9.1',
    kind: 'quality',
    label: 'Corrective action response',
    value: '10 business days',
    detail:
      'Vitrex must respond to a supplier corrective action request within 10 business days with the cause, the immediate fix and a corrective action plan.',
    quote:
      'shall respond within ten (10) business days after receipt with a documented root cause investigation',
    confidence: 0.98,
    enforced: false,
    rule: { kind: 'notice', days: 10 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'QA-VITX-2023-004',
    section: '10.3',
    kind: 'quality',
    label: 'Supplier-identified issues',
    value: 'Notify within 24 hours',
    detail:
      'Vitrex must tell Pillar within 24 hours of any issue that could affect product safety or performance.',
    quote:
      'Supplier shall notify Buyer within twenty-four (24) hours of becoming aware of any issue, wherever identified, that could affect the safety or performance of the Product',
    confidence: 0.95,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'QA-VITX-2023-004',
    section: '11.1',
    kind: 'quality',
    label: 'Record retention',
    value: '10 years',
    detail:
      'Vitrex must retain build, test, change and distribution records for at least 10 years after the lot date code.',
    quote:
      'Supplier shall retain build records, test records, CoCs, change records and distribution records for the Product for at least ten (10) years after the date code of the Lot',
    confidence: 0.94,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'QA-VITX-2023-004',
    section: '12.3',
    kind: 'governing_law',
    label: 'Governing law',
    value: 'New York',
    detail:
      'New York law governs, with exclusive jurisdiction in New York County courts.',
    quote:
      'This Agreement is governed by the laws of the State of New York, without regard to its conflict of laws principles',
    confidence: 0.99,
    enforced: false,
    risk: 'standard',
  }),

  // ---------------------------------------------------------------------------
  // SA-LUMN-2025-002
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-LUMN-2025-002',
    section: '2.1',
    kind: 'auto_renewal',
    label: 'Term and expiry',
    value: 'Expires 2027-06-30, no auto-renewal',
    detail:
      'The agreement runs to June 30, 2027 and can be extended only by written amendment.',
    quote: 'continues until June 30, 2027',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-LUMN-2025-002',
    section: '4.1',
    kind: 'pricing',
    label: 'Unit price',
    value: '$5,500 each, delivered, duties paid',
    detail:
      'The price is $5,500.00 per motor drive (50 HP), delivered with duties and tariffs paid by the supplier, and fixed for the term with no adjustment for any cost change.',
    quote:
      'The price for the Product is Five Thousand Five Hundred Dollars ($5,500.00) per drive, delivered duty paid to the Delivery Point',
    confidence: 0.99,
    enforced: true,
    rule: { kind: 'unit_price', price: 5500, unit: 'each', currency: 'USD' },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-LUMN-2025-002',
    section: '4.2',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 60',
    detail: 'Pillar pays within 60 days after receiving a valid invoice.',
    quote:
      'Buyer shall pay each undisputed invoice within sixty (60) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_60,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-LUMN-2025-002',
    section: '5.1',
    kind: 'incoterms',
    label: 'Delivery terms',
    value:
      'Delivered, duties and tariffs paid: Hayward DC or Dallas panel shop',
    detail:
      'Lumen Drive delivers to the Hayward DC or the Dallas control panel shop with duties and tariffs paid, as each PO states, and bears freight, insurance and import clearance.',
    quote:
      'Supplier shall deliver all Product DDP (Delivered Duty Paid, Incoterms 2020) to Buyer’s Hayward DC in Hayward, California',
    confidence: 0.99,
    enforced: true,
    rule: { kind: 'freight_included', incoterm: 'DDP' },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-LUMN-2025-002',
    section: '5.3',
    kind: 'duties_taxes',
    label: 'Duties and tariffs',
    value: 'Borne by supplier, included in price',
    detail:
      'All duties, tariffs and import taxes, including any new or increased ones, are Lumen Drive’s cost and cannot be added to an invoice.',
    quote:
      'All duties, tariffs, import taxes and other charges imposed on the importation of the Product into the United States, whenever imposed and including any new or increased charge introduced after the Effective Date (for example, an additional duty under Section 232 or Section 301), are borne by Supplier and are included in the Price',
    confidence: 0.99,
    enforced: true,
    rule: { kind: 'duties_included' },
    risk: 'favorable',
    riskNote: 'Protects Pillar against tariff pass-through surcharges.',
  }),
  clause({
    contractId: 'SA-LUMN-2025-002',
    section: '6.4',
    kind: 'change_notification',
    label: 'Change notification',
    value: '180 days prior notice',
    detail:
      'Lumen Drive must give 180 days of notice before changing site, process, firmware or critical component source, and may not change until Pillar approves.',
    quote:
      'Supplier shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in the manufacturing site, manufacturing process, firmware or hardware revision, or source of any critical component (including power semiconductors, capacitors and control boards) used to make the Product',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'notice', days: 180 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-LUMN-2025-002',
    section: '7.1',
    kind: 'safety_stock',
    label: 'Reserve stock',
    value: '90 days, held at supplier',
    detail:
      'Lumen Drive holds a dedicated 90-day reserve of finished motor drives at its Ningbo warehouse, at its own cost.',
    quote:
      'Supplier shall hold, at its own cost at its warehouse in Ningbo, China, a dedicated stock of finished Product, released against the Specification, equal to ninety (90) days of Buyer’s forecast requirements',
    confidence: 0.97,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-LUMN-2025-002',
    section: '10.3',
    kind: 'insurance',
    label: 'Insurance',
    value: '$10M product liability + cargo',
    detail:
      'Lumen Drive must carry $10 million product liability and cargo insurance for transit to the Hayward DC.',
    quote:
      'product liability insurance of not less than $10,000,000 per occurrence',
    confidence: 0.94,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-LUMN-2025-002',
    section: '11.2',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Buyer, 120 days notice',
    detail:
      'Pillar can terminate for convenience on 120 days of notice; Lumen Drive cannot.',
    quote:
      'Buyer may terminate this Agreement for convenience on one hundred twenty (120) days’ prior written notice to Supplier',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'notice', days: 120 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-LUMN-2025-002',
    section: '12.1',
    kind: 'governing_law',
    label: 'Governing law and arbitration',
    value: 'New York law; international arbitration in New York',
    detail:
      'New York law governs and disputes are settled by international arbitration (International Chamber of Commerce rules) in New York.',
    quote: 'This Agreement is governed by the laws of the State of New York',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),

  // ---------------------------------------------------------------------------
  // SA-AURE-2024-009
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-AURE-2024-009',
    section: '2.1',
    kind: 'auto_renewal',
    label: 'Term and expiry',
    value: 'Expires 2027-03-31, no auto-renewal',
    detail:
      'The agreement ends on March 31, 2027 and does not renew automatically.',
    quote: 'continues until March 31, 2027',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-AURE-2024-009',
    section: '3.1',
    kind: 'pricing',
    label: 'Unit price',
    value: '$88.70 per fixture',
    detail:
      'The price is $88.70 per Pillar Pro LED high-bay fixture, fixed for the term with no surcharges.',
    quote:
      'The price for the Product is Eighty-Eight Dollars and Seventy Cents ($88.70) per Fixture',
    confidence: 0.98,
    enforced: true,
    rule: {
      kind: 'unit_price',
      price: 88.7,
      unit: 'fixture',
      currency: 'USD',
    },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-AURE-2024-009',
    section: '4.2',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail: 'Pillar pays within 45 days after receiving a valid invoice.',
    quote:
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-AURE-2024-009',
    section: '4.3',
    kind: 'early_pay_discount',
    label: 'Early payment discount',
    value: '2% off in 10 days, else Net 45',
    detail:
      'Pillar may deduct 2% from an invoice by paying within 10 days of receiving it.',
    quote:
      'If Buyer pays an invoice within ten (10) days after its receipt of the valid invoice, Buyer may deduct from the invoice total a discount of two percent (2%)',
    confidence: 0.98,
    enforced: true,
    rule: {
      kind: 'payment_terms',
      netDays: 45,
      discount: { pct: 2, days: 10 },
    },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-AURE-2024-009',
    section: '4.6',
    kind: 'price_adjustment',
    label: 'Recyclable packaging roadmap',
    value: 'Recyclable option by 2027-03-31, premium ≤3%',
    detail:
      'Aurelia must deliver a roadmap to recyclable, plastic-free packaging, with commercial supply by March 31, 2027 at no more than a 3% price premium.',
    quote: 'commercial supply of the recyclable packaging by March 31, 2027',
    confidence: 0.94,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-AURE-2024-009',
    section: '5.1',
    kind: 'incoterms',
    label: 'Delivery terms',
    value: 'Delivered, freight included: Dallas / Phoenix / Hayward DC',
    detail:
      'Aurelia delivers to the Dallas, Phoenix or Hayward DC with freight included, and bears freight, insurance and export clearance.',
    quote:
      'Supplier shall deliver all Product DAP (Delivered at Place, Incoterms 2020) to Buyer’s Dallas DC in Dallas, Texas, Phoenix DC in Phoenix, Arizona or Hayward DC in Hayward, California',
    confidence: 0.99,
    enforced: true,
    rule: { kind: 'freight_included', incoterm: 'DAP' },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-AURE-2024-009',
    section: '6.1',
    kind: 'lead_time',
    label: 'Lead time',
    value: '6 weeks from PO',
    detail: 'Aurelia must deliver within 6 weeks of the purchase order date.',
    quote:
      'Supplier shall deliver the Product to the Delivery Point within six (6) weeks after the date of Buyer’s purchase order',
    confidence: 0.97,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-AURE-2024-009',
    section: '6.2',
    kind: 'sla',
    label: 'On time, in full target',
    value: '95% on time, in full',
    detail:
      'Aurelia must deliver at least 95% of order lines on time and in full, and provide a recovery plan if a quarter falls short.',
    quote:
      'Supplier shall deliver at least ninety-five percent (95%) of purchase order lines on time and in full, measured monthly',
    confidence: 0.96,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-AURE-2024-009',
    section: '7.4',
    kind: 'change_notification',
    label: 'Change notification',
    value: '180 days prior notice',
    detail:
      'Aurelia must give 180 days of notice before changing site, LED package or driver supplier, housing alloy, optics or process.',
    quote:
      'Supplier shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in the manufacturing site, the LED package or driver supplier, the aluminium housing alloy or supplier, the optics or the manufacturing process for the Product',
    confidence: 0.96,
    enforced: false,
    rule: { kind: 'notice', days: 180 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-AURE-2024-009',
    section: '12.2',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Buyer, 120 days notice',
    detail:
      'Pillar can terminate for convenience on 120 days of notice; Aurelia cannot.',
    quote:
      'Buyer may terminate this Agreement for convenience on one hundred twenty (120) days’ prior written notice to Supplier',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'notice', days: 120 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-AURE-2024-009',
    section: '13.1',
    kind: 'governing_law',
    label: 'Governing law',
    value: 'New York',
    detail:
      'New York law governs, with exclusive jurisdiction in New York County courts.',
    quote:
      'This Agreement is governed by the laws of the State of New York, without regard to its conflict of laws principles',
    confidence: 0.99,
    enforced: false,
    risk: 'standard',
  }),

  // ---------------------------------------------------------------------------
  // MSA-HALB-2023-011
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'MSA-HALB-2023-011',
    section: '6.1',
    kind: 'rate_card',
    label: 'Agreed hourly rates',
    value: 'Rates per statement of work; unlisted roles need a change order',
    detail:
      'Hourly rates come from each statement of work, and no role or rate that is not listed may be billed without a signed change order.',
    quote:
      'No role, and no rate, that is not set out in the rate card of the applicable SOW may be billed',
    confidence: 0.98,
    enforced: false,
    risk: 'favorable',
    riskNote:
      'Enforced through the hourly rates in each statement of work (for example SOW-HALB-2026-03, Section 4.2).',
  }),
  clause({
    contractId: 'MSA-HALB-2023-011',
    section: '6.2',
    kind: 'not_to_exceed',
    label: 'Budget cap',
    value: 'Fees plus expenses capped per statement of work',
    detail:
      'On time-and-materials statements of work, total fees and expenses cannot exceed the budget cap without a change order.',
    quote:
      'the total of the Fees and expenses billed under it shall not exceed the not-to-exceed amount stated in the SOW',
    confidence: 0.97,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-HALB-2023-011',
    section: '6.3',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 60',
    detail: 'Pillar pays within 60 days after receiving a valid invoice.',
    quote:
      'Client shall pay each undisputed invoice within sixty (60) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_60,
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-HALB-2023-011',
    section: '6.4',
    kind: 'expenses',
    label: 'Expenses cap',
    value: 'Expenses ≤10% of fees, receipts required',
    detail:
      'Reimbursable expenses cannot exceed 10% of professional fees on a statement of work and each must be supported by an itemized receipt.',
    quote:
      'the expenses invoiced under an SOW shall not exceed ten percent (10%) of the professional Fees invoiced under that SOW; and (b) each expense is supported by an itemized receipt',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'max_pct', appliesTo: 'expenses (% of fees)', maxPct: 10 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-HALB-2023-011',
    section: '9.3',
    kind: 'liability_cap',
    label: 'Liability cap',
    value: 'Fees under the statement of work',
    detail:
      "Each party's liability is capped at the fees paid or payable under the statement of work concerned, with carve-outs for indemnities, confidentiality and gross negligence.",
    quote:
      'neither party’s liability under this Agreement shall exceed the Fees paid or payable under the SOW concerned',
    confidence: 0.95,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-HALB-2023-011',
    section: '10.1',
    kind: 'insurance',
    label: 'Insurance',
    value: '$10M professional liability',
    detail:
      'Halbrook must carry $10 million professional liability and $5 million cyber insurance.',
    quote:
      'professional liability insurance of not less than $10,000,000 per claim',
    confidence: 0.94,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-HALB-2023-011',
    section: '11.2',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Client, 30 days notice',
    detail:
      'Pillar can end the master agreement or any statement of work for convenience on 30 days of notice.',
    quote:
      'Client may terminate this Agreement or any SOW for convenience on thirty (30) days’ prior written notice to Halbrook',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'notice', days: 30 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-HALB-2023-011',
    section: '12.1',
    kind: 'governing_law',
    label: 'Governing law',
    value: 'New York',
    detail:
      'New York law governs, with exclusive jurisdiction in New York County courts.',
    quote:
      'This Agreement is governed by the laws of the State of New York, without regard to its conflict of laws principles',
    confidence: 0.99,
    enforced: false,
    risk: 'standard',
  }),

  // ---------------------------------------------------------------------------
  // SOW-HALB-2026-03
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SOW-HALB-2026-03',
    section: '3.2',
    kind: 'termination',
    label: 'Statement of work term',
    value: '2026-05-01 to 2026-11-30',
    detail: 'The services run from May 1, 2026 to November 30, 2026.',
    quote:
      'The Services under this SOW begin on May 1, 2026 and end on November 30, 2026',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SOW-HALB-2026-03',
    section: '4.2',
    kind: 'rate_card',
    label: 'Agreed hourly rates',
    value: 'Partner $950, Principal $750, Consultant $420, Analyst $280',
    detail:
      'Four roles are billable, at fixed hourly rates; any other role needs a written change order before it is used or billed.',
    quote:
      'Partner, $950 per hour; Principal, $750 per hour; Consultant, $420 per hour; and Analyst, $280 per hour',
    confidence: 0.99,
    enforced: true,
    rule: {
      kind: 'rate_card',
      rates: [
        { role: 'Partner', rate: 950 },
        { role: 'Principal', rate: 750 },
        { role: 'Consultant', rate: 420 },
        { role: 'Analyst', rate: 280 },
      ],
    },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SOW-HALB-2026-03',
    section: '5.2',
    kind: 'not_to_exceed',
    label: 'Budget cap',
    value: '$450,000 (fees + expenses)',
    detail:
      'Total fees and expenses on the statement of work cannot exceed $450,000 unless a change order is signed before the work is done.',
    quote:
      'The total of the Fees and expenses billed under this SOW shall not exceed Four Hundred Fifty Thousand Dollars ($450,000)',
    confidence: 0.99,
    enforced: true,
    rule: { kind: 'not_to_exceed', amount: 450000 },
    risk: 'favorable',
    riskNote:
      'The cap covers fees and expenses combined, and Halbrook must warn at 80% consumed.',
  }),
  clause({
    contractId: 'SOW-HALB-2026-03',
    section: '5.3',
    kind: 'expenses',
    label: 'Expenses cap',
    value: 'Expenses ≤10% of fees',
    detail:
      'Expenses on this statement of work cannot exceed 10% of professional fees, per Section 6.4 of the master agreement.',
    quote:
      'may not exceed ten percent (10%) of the professional Fees invoiced under this SOW',
    confidence: 0.97,
    enforced: false,
    risk: 'favorable',
    riskNote: 'Checked on invoices through MSA-HALB-2023-011 Section 6.4.',
  }),

  // ---------------------------------------------------------------------------
  // MSA-TRAN-2025-006
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'MSA-TRAN-2025-006',
    section: '4.2',
    kind: 'price_adjustment',
    label: 'Base freight rate changes',
    value: 'Fixed 12 months; then max +3%',
    detail:
      'Base freight rates are fixed for the first 12 months and any later increase is capped at 3% and needs a written amendment.',
    quote:
      'no increase in a lane’s Line Haul rate shall exceed three percent (3%) of the rate in effect during the preceding twelve (12) months',
    confidence: 0.96,
    enforced: false,
    rule: {
      kind: 'max_pct',
      appliesTo: 'base freight rate increase',
      maxPct: 3,
    },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-TRAN-2025-006',
    section: '5.1',
    kind: 'accessorials',
    label: 'Only listed charges',
    value: 'Fuel surcharge + approved extra charges only',
    detail:
      'Only the fuel surcharge and the extra charges listed in the agreement can be billed; any other fee is not payable.',
    quote: 'Carrier shall not invoice any other charge, fee or surcharge',
    confidence: 0.96,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-TRAN-2025-006',
    section: '5.2',
    kind: 'fuel_surcharge',
    label: 'Fuel surcharge cap',
    value: 'Diesel price table, capped at 18% of base freight',
    detail:
      'The fuel surcharge follows the U.S. Department of Energy diesel price table but can never exceed 18% of the base freight charge.',
    quote:
      'the Fuel Surcharge on any Shipment shall not exceed eighteen percent (18%) of the Line Haul charge for that Shipment',
    confidence: 0.98,
    enforced: true,
    rule: {
      kind: 'max_pct',
      appliesTo: 'fuel surcharge (% of base freight)',
      maxPct: 18,
    },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-TRAN-2025-006',
    section: '5.3',
    kind: 'accessorials',
    label: 'Permitted extra charges',
    value: 'Listed services, only if requested and performed',
    detail:
      'Liftgate, extra-stop, driver-assisted and re-delivery charges are payable only for services Pillar requested in advance and the carrier performed.',
    quote:
      'Carrier may charge the following Accessorial Charges, where the service was requested by Shipper in advance and actually performed',
    confidence: 0.95,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-TRAN-2025-006',
    section: '5.4',
    kind: 'accessorials',
    label: 'Waiting time requires signed proof',
    value: 'Waiting time only with signed proof',
    detail:
      'Waiting-time charges are payable only after a 2-hour free period and only if the claim includes proof signed by a Pillar facility representative.',
    quote:
      'Carrier shall not invoice detention without the signed proof, and Shipper is not obliged to pay any detention charge that is not supported by it',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'requires_proof', charge: 'waiting time' },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-TRAN-2025-006',
    section: '6.2',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail: 'Pillar pays within 45 days after receiving a valid invoice.',
    quote:
      'Shipper shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-TRAN-2025-006',
    section: '7.1',
    kind: 'sla',
    label: 'On-time performance',
    value: '95% on-time pickup and delivery',
    detail:
      'The carrier must pick up and deliver at least 95% of shipments on time each month, or provide a corrective action plan.',
    quote:
      'Carrier shall pick up at least ninety-five percent (95%) of Shipments within the scheduled pickup window',
    confidence: 0.96,
    enforced: false,
    risk: 'standard',
    riskNote: 'Current on-time pickup is 94.5%, just under the target.',
  }),
  clause({
    contractId: 'MSA-TRAN-2025-006',
    section: '8.1',
    kind: 'liability_cap',
    label: 'Cargo liability',
    value: 'Full value',
    detail:
      'The carrier is liable for the full actual loss or damage to cargo, with no per-pound limit.',
    quote:
      'to the full extent provided by 49 U.S.C. Section 14706 (the Carmack Amendment), without limitation to a value per pound unless the parties agree otherwise in writing',
    confidence: 0.95,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-TRAN-2025-006',
    section: '9.1',
    kind: 'insurance',
    label: 'Insurance',
    value: '$2M auto liability, $250K cargo',
    detail:
      'The carrier must carry $2 million auto liability, $250,000 cargo per vehicle and $2 million general liability insurance.',
    quote:
      'automobile liability insurance of not less than $2,000,000 per accident',
    confidence: 0.94,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-TRAN-2025-006',
    section: '10.1',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Either party, 30 days notice',
    detail: 'Either party can terminate for convenience on 30 days of notice.',
    quote:
      'Either party may terminate this Agreement for convenience on thirty (30) days’ prior written notice',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'notice', days: 30 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-TRAN-2025-006',
    section: '11.1',
    kind: 'governing_law',
    label: 'Governing law',
    value: 'New York',
    detail:
      'New York law governs, with exclusive jurisdiction in New York County courts.',
    quote:
      'This Agreement is governed by the laws of the State of New York, without regard to its conflict of laws principles',
    confidence: 0.99,
    enforced: false,
    risk: 'standard',
  }),

  // ---------------------------------------------------------------------------
  // EA-STRT-2024-001
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'EA-STRT-2024-001',
    section: '3.1',
    kind: 'committed_spend',
    label: 'Annual committed spend',
    value: '$12M per contract year',
    detail:
      'Pillar commits to spend at least $12 million per year on the services.',
    quote:
      'Customer commits to spend not less than Twelve Million Dollars ($12,000,000) on the Services in each Contract Year',
    confidence: 0.99,
    enforced: false,
    risk: 'unfavorable',
    riskNote:
      'Consumption is tracking 18% under the commitment, about $2.16M a year of unused spend.',
  }),
  clause({
    contractId: 'EA-STRT-2024-001',
    section: '3.3',
    kind: 'committed_spend',
    label: 'Unused commitment and rollover',
    value: 'Unused spend lost; up to 10% rolls over',
    detail:
      'Unused commitment is not refunded, but up to 10% of the annual commitment ($1.2M) can be carried into the next year.',
    quote:
      'up to ten percent (10%) of the Annual Committed Spend (One Million Two Hundred Thousand Dollars ($1,200,000)) may be carried forward and applied to Eligible Usage in the next Contract Year',
    confidence: 0.96,
    enforced: false,
    risk: 'unfavorable',
  }),
  clause({
    contractId: 'EA-STRT-2024-001',
    section: '4.2',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail: 'Pillar pays within 45 days after receiving a valid invoice.',
    quote:
      'Customer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'standard',
  }),
  clause({
    contractId: 'EA-STRT-2024-001',
    section: '5.2',
    kind: 'auto_renewal',
    label: 'Automatic renewal',
    value: 'Auto-renews 36 months; 90-day notice',
    detail:
      'The agreement renews automatically for 36 months unless Pillar gives written notice at least 90 days before January 31, 2027, which makes the deadline November 2, 2026.',
    quote:
      'unless Customer gives Stratus written notice of non-renewal at least ninety (90) days before the end of the Initial Term. Accordingly, notice of non-renewal of the Initial Term must be delivered no later than November 2, 2026',
    confidence: 0.99,
    enforced: false,
    rule: { kind: 'notice', days: 90 },
    risk: 'unfavorable',
    riskNote:
      'A missed notice commits $36M over 36 months. Notice must go by courier, email alone is not effective.',
  }),
  clause({
    contractId: 'EA-STRT-2024-001',
    section: '5.5',
    kind: 'termination',
    label: 'No termination for convenience',
    value: 'Only by paying the unpaid commitment',
    detail:
      'Pillar cannot terminate for convenience except by paying the unpaid balance of the current year commitment.',
    quote:
      'Customer may not terminate this Agreement for convenience during the Initial Term or the Renewal Term',
    confidence: 0.96,
    enforced: false,
    risk: 'unfavorable',
  }),
  clause({
    contractId: 'EA-STRT-2024-001',
    section: '6.1',
    kind: 'sla',
    label: 'Availability commitment',
    value: '99.95% monthly',
    detail:
      'Each production service must be available at least 99.95% of every month.',
    quote:
      'Stratus shall make each production Service available to Customer with Monthly Availability of at least 99.95% in each calendar month',
    confidence: 0.99,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'EA-STRT-2024-001',
    section: '6.2',
    kind: 'service_credits',
    label: 'Fee credit for missed uptime',
    value: '10% of monthly fees back if below 99.95%',
    detail:
      'If monthly availability is below 99.95%, Stratus credits 10% of that month’s fees, which is $100,000 at the committed spend.',
    quote:
      'If Monthly Availability in any calendar month is below 99.95%, Stratus shall credit Customer ten percent (10%) of the monthly fees for that month',
    confidence: 0.98,
    enforced: true,
    rule: {
      kind: 'sla_credit',
      target: 99.95,
      tiers: [{ below: 99.95, creditPct: 10 }],
    },
    risk: 'standard',
  }),
  clause({
    contractId: 'EA-STRT-2024-001',
    section: '6.3',
    kind: 'service_credits',
    label: 'Credits applied automatically',
    value: 'Applied automatically, no claim needed',
    detail:
      'Stratus must apply credits to the next invoice on its own, without Pillar having to claim them.',
    quote:
      'apply it automatically to the next invoice issued after the end of the month concerned, without any claim or request by Customer',
    confidence: 0.97,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'EA-STRT-2024-001',
    section: '7.3',
    kind: 'security',
    label: 'Security incident notice',
    value: 'Within 72 hours',
    detail:
      'Stratus must notify Pillar of a security incident within 72 hours after becoming aware of it.',
    quote: 'within seventy-two (72) hours after becoming aware of it',
    confidence: 0.97,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'EA-STRT-2024-001',
    section: '9.3',
    kind: 'liability_cap',
    label: 'Liability cap',
    value: 'Greater of 12 months fees or $12M',
    detail:
      'Liability is capped at the greater of the last 12 months of fees and $12 million, with carve-outs for indemnities, confidentiality and gross negligence.',
    quote:
      'limited to the greater of the fees paid and payable under this Agreement in the twelve (12) months preceding the claim and $12,000,000',
    confidence: 0.95,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'EA-STRT-2024-001',
    section: '11.1',
    kind: 'governing_law',
    label: 'Governing law',
    value: 'Delaware',
    detail:
      'Delaware law governs, with exclusive jurisdiction in Delaware courts.',
    quote:
      'This Agreement is governed by the laws of the State of Delaware, without regard to its conflict of laws principles',
    confidence: 0.99,
    enforced: false,
    risk: 'standard',
  }),

  // ---------------------------------------------------------------------------
  // SA-HOLL-2024-021
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-HOLL-2024-021',
    section: '2.1',
    kind: 'pricing',
    label: 'Unit price',
    value: '$437.00 per 100 ft, copper building wire',
    detail:
      'Copper building wire is $437.00 per 100 ft and the 800 A circuit breaker (a qualified backup source) is $4,020.00 each, fixed for the term.',
    quote:
      'The price for 1/0 AWG THHN/THWN-2 copper building wire, Buyer part number THHN-1/0-CU, is $437.00 per 100 linear feet',
    confidence: 0.99,
    enforced: true,
    rule: { kind: 'unit_price', price: 437, unit: 'C', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-HOLL-2024-021',
    section: '3.1',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail: 'Pillar pays within 45 days after receiving a valid invoice.',
    quote:
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-HOLL-2024-021',
    section: '4.1',
    kind: 'incoterms',
    label: 'Delivery terms',
    value: 'Delivered, freight included: Dallas DC / Phoenix DC / panel shop',
    detail:
      'Hollis delivers with freight included and bears all freight and insurance, with no separate freight charge.',
    quote:
      'Supplier shall deliver the Products DAP (Delivered at Place, Incoterms 2020) to Buyer’s distribution center in Dallas, Texas or Phoenix, Arizona',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'freight_included', incoterm: 'DAP' },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-HOLL-2024-021',
    section: '4.2',
    kind: 'lead_time',
    label: 'Lead time',
    value: '5 weeks from PO',
    detail: 'Hollis must deliver within 5 weeks of the purchase order date.',
    quote:
      'Supplier shall deliver the Products within five (5) weeks after the date of Buyer’s purchase order',
    confidence: 0.97,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-HOLL-2024-021',
    section: '5.1',
    kind: 'change_notification',
    label: 'Change notification',
    value: '180 days prior notice',
    detail:
      'Hollis must give 180 days of notice before changing plant, process or copper-rod or component source.',
    quote:
      'shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in manufacturing plant, process or copper-rod or component source',
    confidence: 0.96,
    enforced: false,
    rule: { kind: 'notice', days: 180 },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // SA-KAIS-2025-003
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-KAIS-2025-003',
    section: '2.1',
    kind: 'pricing',
    label: 'Unit price',
    value: '$448.00 per 100 ft, Pillar collects in Osaka',
    detail:
      'The price is $448.00 per 100 ft with Pillar collecting in Osaka, so Pillar arranges and pays for carriage and import duties.',
    quote:
      'The price for the Product is $448.00 per 100 linear feet, FCA (Free Carrier) Osaka (Incoterms 2020)',
    confidence: 0.99,
    enforced: true,
    rule: { kind: 'unit_price', price: 448, unit: 'C', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-KAIS-2025-003',
    section: '3.1',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 60',
    detail: 'Pillar pays within 60 days after receiving a valid invoice.',
    quote:
      'Buyer shall pay each undisputed invoice within sixty (60) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_60,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-KAIS-2025-003',
    section: '4.1',
    kind: 'incoterms',
    label: 'Delivery terms',
    value: 'Pillar collects in Osaka (Pillar pays freight)',
    detail:
      'Kaisei makes each order available for collection at Osaka, cleared for export, and risk passes when it is loaded on Pillar’s carrier.',
    quote:
      'Supplier shall make each order available for collection by Buyer’s carrier at its Osaka plant, cleared for export',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'freight_included', incoterm: 'FCA' },
    risk: 'standard',
    riskNote:
      'Freight and import duty are for Pillar, which makes the delivered cost ($462.30 per 100 ft) higher than the unit price.',
  }),
  clause({
    contractId: 'SA-KAIS-2025-003',
    section: '4.2',
    kind: 'lead_time',
    label: 'Lead time',
    value: '12 weeks from PO',
    detail:
      'Kaisei must make the product available within 12 weeks of the purchase order date.',
    quote:
      'Supplier shall make the Product available for collection within twelve (12) weeks after the date of Buyer’s purchase order',
    confidence: 0.97,
    enforced: false,
    risk: 'unfavorable',
  }),
  clause({
    contractId: 'SA-KAIS-2025-003',
    section: '5.1',
    kind: 'change_notification',
    label: 'Change notification',
    value: '180 days prior notice',
    detail:
      'Kaisei must give 180 days of notice before changing plant, process or copper-rod source.',
    quote:
      'shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in manufacturing plant, process or copper-rod source',
    confidence: 0.96,
    enforced: false,
    rule: { kind: 'notice', days: 180 },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // SA-NGTE-2024-012
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-NGTE-2024-012',
    section: '1.1',
    kind: 'auto_renewal',
    label: 'Term and expiry',
    value: 'Expires 2027-05-31, no auto-renewal',
    detail:
      'The agreement ends on May 31, 2027 and does not renew automatically.',
    quote: 'continues until May 31, 2027',
    confidence: 0.97,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-NGTE-2024-012',
    section: '2.1',
    kind: 'pricing',
    label: 'Unit price',
    value: '$89.95 per fixture',
    detail:
      'The price is $89.95 per Pillar Pro LED high-bay fixture, fixed for the term and delivered.',
    quote: 'The price for the Product is $89.95 per fixture',
    confidence: 0.99,
    enforced: true,
    rule: {
      kind: 'unit_price',
      price: 89.95,
      unit: 'fixture',
      currency: 'USD',
    },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-NGTE-2024-012',
    section: '2.2',
    kind: 'rebate',
    label: 'Volume rebate',
    value: '1.5% of all purchases once the year passes $8M',
    detail:
      'Once annual purchases pass $8 million, Northgate credits 1.5% of all purchases in the year, retroactive to the first dollar, by credit memo without a claim by Pillar.',
    quote:
      'Once Buyer’s Net Purchases in a calendar year exceed Eight Million Dollars ($8,000,000), Supplier shall pay Buyer a volume rebate equal to one and one-half percent (1.5%) of all Net Purchases in that calendar year, retroactive to the first dollar and not only on the amount above the threshold',
    confidence: 0.97,
    enforced: true,
    rule: { kind: 'rebate', pct: 1.5, threshold: 8000000 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-NGTE-2024-012',
    section: '3.1',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 30',
    detail: 'Pillar pays within 30 days after receiving a valid invoice.',
    quote:
      'Buyer shall pay each undisputed invoice within thirty (30) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: { kind: 'payment_terms', netDays: 30 },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-NGTE-2024-012',
    section: '4.1',
    kind: 'incoterms',
    label: 'Delivery terms',
    value: 'Delivered, freight included: Dallas / Phoenix / Hayward DC',
    detail:
      'Northgate delivers with freight included and bears all freight and insurance, with no separate freight charge.',
    quote:
      'Supplier shall deliver the Product DAP (Delivered at Place, Incoterms 2020) to Buyer’s Dallas DC in Dallas, Texas, Phoenix DC in Phoenix, Arizona or Hayward DC in Hayward, California',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'freight_included', incoterm: 'DAP' },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // SA-POLR-2022-008
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-POLR-2022-008',
    section: '1.1',
    kind: 'auto_renewal',
    label: 'Yearly renewal',
    value: 'Renews yearly, no fixed end date',
    detail:
      'The agreement has no expiry date and continues until one party terminates it.',
    quote:
      'continues indefinitely until terminated under Section 5.1 (an evergreen term)',
    confidence: 0.97,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-POLR-2022-008',
    section: '2.1',
    kind: 'price_adjustment',
    label: 'Annual price review',
    value: 'Increases need 90 days notice',
    detail:
      'Prices are reviewed once a year, and no increase takes effect unless agreed in writing at least 90 days ahead.',
    quote:
      'no increase is effective unless agreed in writing at least ninety (90) days before it takes effect',
    confidence: 0.95,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-POLR-2022-008',
    section: '3.1',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail: 'Pillar pays within 45 days after receiving a valid invoice.',
    quote:
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-POLR-2022-008',
    section: '5.1',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Either party, 12 months notice',
    detail:
      'Either party can terminate for convenience on 12 months of written notice.',
    quote:
      'Either party may terminate this Agreement for convenience on twelve (12) months’ written notice',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'notice', days: 365 },
    risk: 'unfavorable',
    riskNote:
      'Polaris is the only supplier of weatherproof (NEMA 4X) enclosures, so a supplier-initiated exit would leave a long gap while a new supplier is qualified.',
  }),

  // ---------------------------------------------------------------------------
  // SA-MERI-2025-001
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-MERI-2025-001',
    section: '2.1',
    kind: 'price_adjustment',
    label: 'Quarterly index pricing',
    value: 'PVC resin market price + $0.11/lb, reset quarterly',
    detail:
      'The price resets each quarter to the posted PVC resin market price plus an $0.11 per pound manufacturing add-on on the PVC compound in the conduit.',
    quote:
      'The price for the Product is reset each calendar quarter at the posted PVC resin index price for the quarter plus a fixed conversion adder of eleven cents ($0.11) per pound of PVC compound in the Product',
    confidence: 0.96,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-MERI-2025-001',
    section: '3.1',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail: 'Pillar pays within 45 days after receiving a valid invoice.',
    quote:
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-MERI-2025-001',
    section: '4.1',
    kind: 'incoterms',
    label: 'Delivery terms',
    value: 'Delivered, freight included: Dallas DC',
    detail:
      'Meridian delivers by truckload to the Dallas DC and bears all freight.',
    quote:
      'Supplier shall deliver the Product by truckload DAP (Delivered at Place, Incoterms 2020) to Buyer’s Dallas distribution center in Dallas, Texas',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'freight_included', incoterm: 'DAP' },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-MERI-2025-001',
    section: '5.1',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Buyer, 90 days notice',
    detail: 'Pillar can terminate for convenience on 90 days of notice.',
    quote:
      'Buyer may terminate for convenience on ninety (90) days’ written notice',
    confidence: 0.96,
    enforced: false,
    rule: { kind: 'notice', days: 90 },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // SA-CLRW-2024-006
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-CLRW-2024-006',
    section: '2.1',
    kind: 'pricing',
    label: 'Fixed price schedule',
    value: 'Fixed for the term, no surcharges',
    detail:
      'Prices come from the agreed schedule and are fixed for the term with no surcharges.',
    quote: 'Supplier shall not add any surcharge to a price',
    confidence: 0.96,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-CLRW-2024-006',
    section: '3.1',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail: 'Pillar pays within 45 days after receiving a valid invoice.',
    quote:
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-CLRW-2024-006',
    section: '4.1',
    kind: 'incoterms',
    label: 'Delivery terms',
    value: 'Delivered, freight included: Dallas DC',
    detail:
      'Clearwell delivers to the Dallas DC within 3 weeks and bears all freight.',
    quote:
      'Supplier shall deliver the Products DAP (Delivered at Place, Incoterms 2020) to Buyer’s Dallas distribution center in Dallas, Texas',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'freight_included', incoterm: 'DAP' },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-CLRW-2024-006',
    section: '4.2',
    kind: 'change_notification',
    label: 'Change notification',
    value: '180 days prior notice',
    detail:
      'Clearwell must give 180 days of notice before changing site, process or raw-material source.',
    quote:
      'shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in manufacturing site, process or raw-material source',
    confidence: 0.96,
    enforced: false,
    rule: { kind: 'notice', days: 180 },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // SA-VITX-2023-004
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-VITX-2023-004',
    section: '1.1',
    kind: 'auto_renewal',
    label: 'Term and expiry',
    value: 'Expires 2027-06-30, no auto-renewal',
    detail:
      'The agreement expires on June 30, 2027 and does not renew automatically.',
    quote: 'expires on June 30, 2027',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-VITX-2023-004',
    section: '2.1',
    kind: 'pricing',
    label: 'Unit price',
    value: '$3,850 each, delivered to Dallas',
    detail:
      'The price is $3,850.00 per breaker, delivered to Dallas, with no separate freight or surcharges.',
    quote: 'The price for the Product is $3,850.00 each',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'unit_price', price: 3850, unit: 'each', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-VITX-2023-004',
    section: '3.1',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 60',
    detail: 'Pillar pays within 60 days after receiving a valid invoice.',
    quote:
      'Buyer shall pay each undisputed invoice within sixty (60) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_60,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-VITX-2023-004',
    section: '4.2',
    kind: 'change_notification',
    label: 'Change notice and approval',
    value: '180 days notice + approval',
    detail:
      'Changes of site, subcontractor or component source follow Section 7.2 of the Quality Agreement, and breakers must ship only from factories UL has certified.',
    quote:
      'which requires one hundred eighty (180) days’ prior written notice and Buyer’s written approval',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'notice', days: 180 },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // SA-STLR-2024-005
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-STLR-2024-005',
    section: '1.2',
    kind: 'lead_time',
    label: 'Order acknowledgment turnaround',
    value: '5 business days',
    detail:
      'Stellar must return approved submittal drawings and an order acknowledgment with a confirmed ship date within 5 business days of each purchase order.',
    quote:
      'with a turnaround of not more than five (5) business days from receipt of each purchase order',
    confidence: 0.96,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-STLR-2024-005',
    section: '2.2',
    kind: 'price_adjustment',
    label: 'Regulatory change',
    value: '120 days notice; no price change without amendment',
    detail:
      'Stellar must give 120 days of notice of efficiency-rule or core steel changes that affect price, capacity or supply, and no price change is effective without a signed amendment.',
    quote:
      'No change in price is effective unless agreed in a written amendment signed by Buyer',
    confidence: 0.96,
    enforced: false,
    rule: { kind: 'notice', days: 120 },
    risk: 'favorable',
    riskNote:
      'Protects Pillar against cost increases from the federal energy efficiency rule or specialty steel being passed on without agreement.',
  }),
  clause({
    contractId: 'SA-STLR-2024-005',
    section: '3.1',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail: 'Pillar pays within 45 days after receiving a valid invoice.',
    quote:
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-STLR-2024-005',
    section: '4.1',
    kind: 'change_notification',
    label: 'Change notification',
    value: '180 days prior notice',
    detail:
      'Stellar must give 180 days of notice before changing plant, process or core steel source.',
    quote:
      'shall give Buyer at least one hundred eighty (180) days’ prior written notice of any change in manufacturing plant, process or source of grain-oriented electrical steel',
    confidence: 0.96,
    enforced: false,
    rule: { kind: 'notice', days: 180 },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // MSA-BRTL-2025-004
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'MSA-BRTL-2025-004',
    section: '2.1',
    kind: 'pricing',
    label: 'Milestone billing',
    value: 'Invoice only on accepted milestones',
    detail:
      'Brightline can invoice fees only after Pillar accepts the milestone in writing.',
    quote:
      'Agency shall invoice fees only on Client’s written acceptance of the milestone to which they relate',
    confidence: 0.97,
    enforced: true,
    rule: {
      kind: 'requires_proof',
      charge: 'milestone fee (written acceptance)',
    },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-BRTL-2025-004',
    section: '2.2',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail: 'Pillar pays within 45 days after receiving a valid invoice.',
    quote:
      'Client shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-BRTL-2025-004',
    section: '2.3',
    kind: 'markup',
    label: 'Third-party pass-through',
    value: 'At cost, no markup, pre-approved',
    detail:
      'Media, production and talent costs pass through at cost and only if the estimate was approved in advance.',
    quote:
      'are passed through at cost, without markup, and only if Client has approved the estimate in writing before they are incurred',
    confidence: 0.96,
    enforced: true,
    rule: {
      kind: 'max_pct',
      appliesTo: 'third-party pass-through markup',
      maxPct: 0,
    },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-BRTL-2025-004',
    section: '4.1',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Client, 30 days notice',
    detail:
      'Pillar can end the agreement or any statement of work for convenience on 30 days of notice.',
    quote:
      'Client may terminate it or any SOW for convenience on thirty (30) days’ written notice',
    confidence: 0.96,
    enforced: false,
    rule: { kind: 'notice', days: 30 },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // CPA-NSTR-2025-002
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'CPA-NSTR-2025-002',
    section: '1.1',
    kind: 'pricing',
    label: 'Catalog price: 14-inch business laptop',
    value: '$1,289 per unit',
    detail:
      'The contract price of the 14-inch business laptop is $1,289.00 each, and other catalog items are list price less 12%.',
    quote:
      'The contract price of the Catalog 14-inch business laptop (16 GB memory, 512 GB solid-state drive, three-year onsite warranty) is $1,289.00 per unit',
    confidence: 0.99,
    enforced: true,
    rule: { kind: 'unit_price', price: 1289, unit: 'unit', currency: 'USD' },
    risk: 'favorable',
  }),
  clause({
    contractId: 'CPA-NSTR-2025-002',
    section: '2.1',
    kind: 'pricing',
    label: 'Freight included',
    value: 'Freight prepaid and included',
    detail:
      'Northstar delivers with freight prepaid and included in the contract price.',
    quote: 'with freight prepaid and included in the contract price',
    confidence: 0.95,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'CPA-NSTR-2025-002',
    section: '3.1',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 30',
    detail: 'Pillar pays within 30 days after receiving a valid invoice.',
    quote:
      'Customer shall pay each undisputed invoice within thirty (30) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: { kind: 'payment_terms', netDays: 30 },
    risk: 'standard',
  }),
  clause({
    contractId: 'CPA-NSTR-2025-002',
    section: '4.1',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Either party, 90 days notice',
    detail:
      'Either party can terminate for convenience on 90 days of notice; the term ends January 31, 2028.',
    quote:
      'Either party may terminate it for convenience on ninety (90) days’ written notice',
    confidence: 0.96,
    enforced: false,
    rule: { kind: 'notice', days: 90 },
    risk: 'standard',
  }),

  // ---------------------------------------------------------------------------
  // MSA-KEYS-2024-010
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'MSA-KEYS-2024-010',
    section: '2.1',
    kind: 'markup',
    label: 'Markup cap',
    value: 'Max 32% over pay rate',
    detail:
      'The markup, which covers all burden, benefits, overhead and margin, cannot exceed 32% of the worker pay rate.',
    quote: 'shall not exceed thirty-two percent (32%) of the Worker’s pay rate',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'max_pct', appliesTo: 'markup (% of pay rate)', maxPct: 32 },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-KEYS-2024-010',
    section: '2.2',
    kind: 'markup',
    label: 'Overtime approval',
    value: 'Overtime needs prior written approval',
    detail:
      'Overtime is billable only if the hiring manager approved it in writing before it was worked.',
    quote:
      'Overtime hours, being hours in excess of forty (40) in a workweek, are billable only if the Client hiring manager has approved them in writing before they are worked',
    confidence: 0.97,
    enforced: true,
    rule: { kind: 'requires_proof', charge: 'overtime' },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-KEYS-2024-010',
    section: '3.1',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail:
      'Pillar pays within 45 days after receiving a valid invoice with approved timesheets.',
    quote:
      'Client shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-KEYS-2024-010',
    section: '4.1',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Client, 10 days notice',
    detail:
      'Pillar can terminate the agreement or end any assignment on 10 days of notice.',
    quote:
      'Client may terminate it or end any assignment for convenience on ten (10) days’ written notice',
    confidence: 0.96,
    enforced: false,
    rule: { kind: 'notice', days: 10 },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // MSA-CLVW-2024-003
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'MSA-CLVW-2024-003',
    section: '1.2',
    kind: 'pricing',
    label: 'Fixed fees',
    value: 'Monthly fixed fee, fixed for term',
    detail:
      'A monthly fixed fee covers scheduled services, and unit prices apply to extra services requested in writing.',
    quote: 'Fees are fixed for the Term',
    confidence: 0.95,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-CLVW-2024-003',
    section: '2.1',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail: 'Pillar pays within 45 days after receiving a valid invoice.',
    quote:
      'Client shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-CLVW-2024-003',
    section: '3.1',
    kind: 'insurance',
    label: 'Insurance',
    value: '$2M general liability',
    detail:
      'Clearview must carry $2 million general liability insurance and name Pillar as additional insured.',
    quote:
      'commercial general liability insurance of not less than $2,000,000 per occurrence',
    confidence: 0.94,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-CLVW-2024-003',
    section: '4.1',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Client, 60 days notice',
    detail:
      'Pillar can terminate for convenience on 60 days of notice; the term ends December 31, 2026.',
    quote:
      'Client may terminate it for convenience on sixty (60) days’ written notice',
    confidence: 0.96,
    enforced: false,
    rule: { kind: 'notice', days: 60 },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // NDA-KEST-2026-001
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'NDA-KEST-2026-001',
    section: '2.1',
    kind: 'confidentiality',
    label: 'Use restriction',
    value: 'Evaluation purpose only',
    detail:
      'Each party may use the other’s confidential information only to evaluate the potential motor drive supply relationship.',
    quote:
      'The Recipient shall use the Discloser’s Confidential Information only for the Purpose',
    confidence: 0.97,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'NDA-KEST-2026-001',
    section: '3.1',
    kind: 'confidentiality',
    label: 'Term and survival',
    value: '2-year exchange period, 5-year survival',
    detail:
      'Information can be exchanged for 2 years, and the confidentiality obligations last 5 years after that.',
    quote:
      'The Parties may exchange Confidential Information for two (2) years after the Effective Date. The obligations in this Agreement continue for five (5) years after the end of that period',
    confidence: 0.97,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'NDA-KEST-2026-001',
    section: '4.2',
    kind: 'governing_law',
    label: 'Governing law',
    value: 'New York',
    detail:
      'New York law governs, with exclusive jurisdiction in New York County courts.',
    quote:
      'This Agreement is governed by the laws of the State of New York, and the courts located in New York County, New York have exclusive jurisdiction over any dispute arising out of it',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),
  // ---------------------------------------------------------------------------
  // SA-MROS-2025-004
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '1.1',
    kind: 'auto_renewal',
    label: 'Term and expiry',
    value: 'Expires 2028-01-31, no auto-renewal',
    detail:
      'The agreement ends on January 31, 2028 and does not renew automatically.',
    quote: 'continues until January 31, 2028',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '3.1',
    kind: 'pricing',
    label: 'Unit price: strut channel',
    value: '$30.00 per length',
    detail:
      'Strut channel, 1-5/8 in x 10 ft, is $30.00 per length. MRO Supply also invoices it as “Unistrut Channel”.',
    quote: 'is Thirty Dollars ($30.00) per length',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'unit_price', price: 30, unit: 'length', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '3.2',
    kind: 'pricing',
    label: 'Unit price: hydraulic quick coupler',
    value: '$24.00 per coupler',
    detail: 'The 1/2 in NPT flat-face hydraulic quick coupler is $24.00 each.',
    quote: 'is Twenty-Four Dollars ($24.00) per coupler',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'unit_price', price: 24, unit: 'coupler', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '3.3',
    kind: 'pricing',
    label: 'Unit price: mounting bracket',
    value: '$22.50 per bracket',
    detail: 'The heavy-duty galvanized mounting bracket is $22.50 each.',
    quote: 'is Twenty-Two Dollars and Fifty Cents ($22.50) per bracket',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'unit_price', price: 22.5, unit: 'bracket', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '3.4',
    kind: 'pricing',
    label: 'Unit price: ball valve',
    value: '$25.00 per valve',
    detail: 'The 1 in NPT brass ball valve is $25.00 each.',
    quote: 'is Twenty-Five Dollars ($25.00) per valve',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'unit_price', price: 25, unit: 'valve', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '3.5',
    kind: 'price_adjustment',
    label: 'Prices fixed',
    value: 'Fixed for the term, no surcharges',
    detail:
      'The four schedule prices are fixed through January 2028 and cannot carry a fuel, handling or administrative surcharge.',
    quote: 'The prices in Sections 3.1 to 3.4 are fixed for the Term',
    confidence: 0.97,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '4.1',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: '2% off in 10 days, else Net 30',
    detail:
      'Pillar may deduct 2% from an invoice by paying within 10 days of receiving it; otherwise payment is due in 30 days.',
    quote:
      'Payment terms are “2% 10, Net 30”: Buyer may deduct two percent (2%) from the invoice total if it pays within ten (10) days after receipt of a valid invoice',
    confidence: 0.98,
    enforced: true,
    rule: {
      kind: 'payment_terms',
      netDays: 30,
      discount: { pct: 2, days: 10 },
    },
    risk: 'standard',
    riskNote:
      'Net 30 is short, but the 2% discount for paying in 10 days is worth about 37% annualised.',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '4.2',
    kind: 'payment_terms',
    label: 'Invoice terms have no effect',
    value: 'Net 30, shorter invoice terms void',
    detail:
      'Payment is due 30 days after receiving a valid invoice, and a shorter term printed on an invoice has no effect.',
    quote:
      'Any shorter payment term stated on an invoice or other Supplier document has no effect',
    confidence: 0.97,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '4.4',
    kind: 'payment_terms',
    label: 'Billing through the parent company',
    value: 'Apex Industrial Group invoices accepted',
    detail:
      'MRO Supply may invoice through its parent, Apex Industrial Group, if the invoice cites the agreement and PO and asks for payment into the account on file.',
    quote:
      'Buyer shall treat a conforming invoice from Apex Industrial Group as an invoice from Supplier',
    confidence: 0.96,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '5.1',
    kind: 'incoterms',
    label: 'Freight paid by supplier',
    value: 'Freight included on orders of $5,000 or more',
    detail:
      'MRO Supply ships orders of $5,000 or more with freight paid by MRO Supply and cannot invoice freight, handling or fuel on them. Below $5,000 it may add freight at actual carrier cost.',
    quote:
      'Supplier shall ship each order of Five Thousand Dollars ($5,000) or more freight prepaid, DAP (Delivered at Place, Incoterms 2020)',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'freight_included', incoterm: 'DAP' },
    risk: 'favorable',
    riskNote:
      'The freight check applies to orders of $5,000 or more. Smaller orders can carry freight at cost.',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '5.2',
    kind: 'incoterms',
    label: 'Buyer-routed orders',
    value: 'Pickup by Pillar’s carrier, no supplier freight',
    detail:
      'Pillar may have its own carrier collect an order from the Houston warehouse. MRO Supply charges no freight, and the carrier bills Pillar directly.',
    quote:
      'Supplier shall have the order ready for pickup on the requested date and shall not charge for freight on it',
    confidence: 0.95,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '6.1',
    kind: 'lead_time',
    label: 'Lead time',
    value: '14 days from PO',
    detail: 'MRO Supply must deliver within 14 days of the purchase order.',
    quote:
      'Supplier shall deliver Products within fourteen (14) days after the date of Buyer’s purchase order',
    confidence: 0.97,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '6.2',
    kind: 'sla',
    label: 'Fill rate',
    value: '95% on time and in full',
    detail:
      'MRO Supply must fill at least 95% of order lines on time and in full, and provide a recovery plan if a quarter falls short.',
    quote:
      'Supplier shall fill at least ninety-five percent (95%) of purchase order lines on time and in full, measured monthly',
    confidence: 0.96,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-MROS-2025-004',
    section: '8.1',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Buyer, 90 days notice',
    detail: 'Pillar can terminate for convenience on 90 days of notice.',
    quote:
      'Buyer may terminate for convenience on ninety (90) days’ written notice to Supplier',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'notice', days: 90 },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // SA-BRST-2024-011
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'SA-BRST-2024-011',
    section: '1.1',
    kind: 'auto_renewal',
    label: 'Term and expiry',
    value: 'Expires 2027-07-31, no auto-renewal',
    detail:
      'The agreement ends on July 31, 2027 and does not renew automatically.',
    quote: 'continues until July 31, 2027',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-BRST-2024-011',
    section: '1.2',
    kind: 'committed_spend',
    label: 'Fill-in supply, no minimum',
    value: 'Backup supplier, no minimum volume',
    detail:
      'Border States covers backorders and shortages. Pillar is not obliged to buy any minimum quantity or share of its requirements.',
    quote:
      'is not obliged to purchase any minimum quantity or share of its requirements',
    confidence: 0.96,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-BRST-2024-011',
    section: '2.1',
    kind: 'lead_time',
    label: 'Ship time',
    value: 'Stocked items ship in 3 business days',
    detail:
      'Border States ships each stocked item within 3 business days and confirms the ship date of any out-of-stock item within 1 business day.',
    quote:
      'Supplier shall ship each stocked item within three (3) business days after receipt of the purchase order',
    confidence: 0.97,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-BRST-2024-011',
    section: '3.1',
    kind: 'pricing',
    label: 'Unit price: steel conduit',
    value: '$28.00 per length',
    detail: 'Steel (EMT) conduit, 3/4 in x 10 ft, is $28.00 per length.',
    quote: 'is Twenty-Eight Dollars ($28.00) per length',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'unit_price', price: 28, unit: 'length', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-BRST-2024-011',
    section: '3.2',
    kind: 'pricing',
    label: 'Unit price: junction box',
    value: '$25.00 per box',
    detail:
      'The 1-gang cast aluminum weatherproof junction box is $25.00 each.',
    quote: 'is Twenty-Five Dollars ($25.00) per box',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'unit_price', price: 25, unit: 'box', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-BRST-2024-011',
    section: '3.3',
    kind: 'pricing',
    label: 'Unit price: liquidtight connector',
    value: '$16.50 per connector',
    detail: 'The 3/4 in straight liquidtight connector is $16.50 each.',
    quote: 'is Sixteen Dollars and Fifty Cents ($16.50) per connector',
    confidence: 0.98,
    enforced: true,
    rule: {
      kind: 'unit_price',
      price: 16.5,
      unit: 'connector',
      currency: 'USD',
    },
    risk: 'standard',
  }),
  clause({
    contractId: 'SA-BRST-2024-011',
    section: '3.4',
    kind: 'price_adjustment',
    label: 'Prices fixed',
    value: 'Fixed for the term, no surcharges',
    detail:
      'The three schedule prices are fixed through July 2027 and cannot carry a fuel, handling or restocking surcharge.',
    quote: 'The prices in Sections 3.1 to 3.3 are fixed for the Term',
    confidence: 0.97,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-BRST-2024-011',
    section: '4.2',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail: 'Pillar pays within 45 days after receiving a valid invoice.',
    quote:
      'Buyer shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'favorable',
    riskNote:
      'Distributor invoices commonly print Net 30. A shorter term printed on an invoice has no effect under this clause.',
  }),
  clause({
    contractId: 'SA-BRST-2024-011',
    section: '5.1',
    kind: 'incoterms',
    label: 'Delivery terms',
    value: 'Delivered, freight included: Pillar DC or branch',
    detail:
      'Border States delivers to the Pillar DC or branch on the purchase order with freight included and bears freight and insurance. No separate freight is payable.',
    quote:
      'Supplier shall deliver all Products DAP (Delivered at Place, Incoterms 2020) to the Buyer distribution center or branch named in the purchase order',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'freight_included', incoterm: 'DAP' },
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-BRST-2024-011',
    section: '6.2',
    kind: 'quality',
    label: 'Returns',
    value: '12 months for defects, 30 days for stock',
    detail:
      'Pillar may return nonconforming product for 12 months at Border States’ cost, and unused stock for 30 days with no restocking fee.',
    quote:
      'Buyer may return any nonconforming Product within twelve (12) months after delivery for replacement or credit at Supplier’s cost',
    confidence: 0.96,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'SA-BRST-2024-011',
    section: '7.1',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Buyer, 60 days notice',
    detail: 'Pillar can terminate for convenience on 60 days of notice.',
    quote:
      'Buyer may terminate for convenience on sixty (60) days’ written notice to Supplier',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'notice', days: 60 },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // MSA-BRZV-2025-002
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'MSA-BRZV-2025-002',
    section: '1.2',
    kind: 'auto_renewal',
    label: 'Term and expiry',
    value: 'Expires 2028-05-31, no auto-renewal',
    detail:
      'The agreement ends on May 31, 2028 and does not renew automatically. Pillar owes no minimum volume.',
    quote: 'continues until May 31, 2028',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-BRZV-2025-002',
    section: '3.1',
    kind: 'pricing',
    label: 'Rate: pallet handling',
    value: '$18.00 per pallet',
    detail: 'Inbound and outbound pallet handling is $18.00 per pallet.',
    quote: 'is Eighteen Dollars ($18.00) per pallet',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'unit_price', price: 18, unit: 'pallet', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-BRZV-2025-002',
    section: '3.2',
    kind: 'pricing',
    label: 'Rate: ambient storage',
    value: '$12.00 per pallet-month',
    detail:
      'Ambient storage is $12.00 per pallet per month, counted on the last day of the month.',
    quote: 'is Twelve Dollars ($12.00) per pallet-month',
    confidence: 0.98,
    enforced: true,
    rule: {
      kind: 'unit_price',
      price: 12,
      unit: 'pallet-month',
      currency: 'USD',
    },
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-BRZV-2025-002',
    section: '3.3',
    kind: 'pricing',
    label: 'Rate: shuttle load',
    value: '$385.00 per load',
    detail:
      'Shuttle transportation between the Bryan warehouse and the Dallas DC is $385.00 per load.',
    quote: 'is Three Hundred Eighty-Five Dollars ($385.00) per load',
    confidence: 0.97,
    enforced: true,
    rule: { kind: 'unit_price', price: 385, unit: 'load', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-BRZV-2025-002',
    section: '3.4',
    kind: 'accessorials',
    label: 'Only listed charges',
    value: 'Listed rates and one extra charge only',
    detail:
      'Rates are fixed for the term, and no administrative, technology, fuel or minimum fee can be billed.',
    quote:
      'The only charges payable by Shipper are those rates and the accessorial charge in Section 4.1',
    confidence: 0.96,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-BRZV-2025-002',
    section: '4.1',
    kind: 'accessorials',
    label: 'Crating and export packaging',
    value: '$600.00 per job, the one allowed extra charge',
    detail:
      'Crating and export packaging is billable at $600.00 per job when Pillar requested it and it was completed. A work order is enough, with no PO line needed.',
    quote:
      'Provider may charge Six Hundred Dollars ($600.00) per job for outbound crating and export packaging of a Shipper shipment',
    confidence: 0.97,
    enforced: true,
    rule: { kind: 'unit_price', price: 600, unit: 'job', currency: 'USD' },
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-BRZV-2025-002',
    section: '4.2',
    kind: 'accessorials',
    label: 'Extra charges require signed proof',
    value: 'Signed work order or packing list',
    detail:
      'An extra charge is payable only if Brazos Valley holds proof signed by a Pillar facility representative that the service was requested and completed.',
    quote:
      'Provider shall not invoice an accessorial charge without the signed proof',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'requires_proof', charge: 'crating and export packaging' },
    risk: 'favorable',
    riskNote:
      'Proof is confirmed from the signed work order on file. The invoice checker does not read it yet.',
  }),
  clause({
    contractId: 'MSA-BRZV-2025-002',
    section: '5.2',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 45',
    detail: 'Pillar pays within 45 days after receiving a valid invoice.',
    quote:
      'Shipper shall pay each undisputed invoice within forty-five (45) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_45,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-BRZV-2025-002',
    section: '6.1',
    kind: 'sla',
    label: 'Service levels',
    value: 'Stored in 24 hours, released in 4 hours, 99.5% accurate',
    detail:
      'Inbound pallets are stored within 24 hours, outbound pallets released within 4 hours of a request, and inventory accuracy is at least 99.5%.',
    quote:
      'Provider shall receive and put away inbound pallets within twenty-four (24) hours after arrival',
    confidence: 0.96,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-BRZV-2025-002',
    section: '6.2',
    kind: 'liability_cap',
    label: 'Care of goods',
    value: 'Replacement value, $2M insurance',
    detail:
      'Brazos Valley is liable for loss or damage to goods in its custody up to replacement value and carries $2M of warehouse and general liability insurance.',
    quote:
      'Provider is liable to Shipper for the actual loss of, or damage to, goods in its custody, up to their replacement value',
    confidence: 0.96,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-BRZV-2025-002',
    section: '7.1',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Either party, 30 days notice',
    detail: 'Either party can terminate for convenience on 30 days of notice.',
    quote:
      'Either party may terminate this Agreement for convenience on thirty (30) days’ written notice',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'notice', days: 30 },
    risk: 'favorable',
  }),

  // ---------------------------------------------------------------------------
  // MSA-SNTL-2025-003
  // ---------------------------------------------------------------------------
  clause({
    contractId: 'MSA-SNTL-2025-003',
    section: '1.2',
    kind: 'auto_renewal',
    label: 'Term and expiry',
    value: 'Expires 2028-08-31, no auto-renewal',
    detail:
      'The agreement ends on August 31, 2028 and does not renew automatically. Pillar owes no minimum volume.',
    quote: 'continues until August 31, 2028',
    confidence: 0.98,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-SNTL-2025-003',
    section: '2.1',
    kind: 'pricing',
    label: 'Base freight: Houston to Dallas DC',
    value: '$1,200.00 per shipment, up to 20,000 lb',
    detail:
      'A Houston to Dallas DC shipment of up to 20,000 pounds is $1,200.00, including pickup, delivery to the dock, tolls and permits.',
    quote: 'is One Thousand Two Hundred Dollars ($1,200.00) per shipment',
    confidence: 0.98,
    enforced: true,
    rule: {
      kind: 'unit_price',
      price: 1200,
      unit: 'shipment',
      currency: 'USD',
    },
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-SNTL-2025-003',
    section: '2.2',
    kind: 'price_adjustment',
    label: 'Rates fixed',
    value: 'Fixed for the term',
    detail:
      'Rates are fixed through August 2028, and a change needs a signed amendment.',
    quote:
      'Rates are fixed for the Term, and no change is effective unless agreed in a written amendment signed by both parties',
    confidence: 0.97,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-SNTL-2025-003',
    section: '3.1',
    kind: 'fuel_surcharge',
    label: 'Fuel surcharge cap',
    value: 'Above $4.00 diesel only, capped at 15% of base freight',
    detail:
      'A fuel surcharge applies only when the U.S. Department of Energy diesel price is above $4.00 per gallon, and can never exceed 15% of the base freight charge.',
    quote:
      'The fuel surcharge on any Shipment shall not exceed fifteen percent (15%) of the Line Haul charge for that Shipment',
    confidence: 0.98,
    enforced: true,
    rule: {
      kind: 'max_pct',
      appliesTo: 'fuel surcharge (% of base freight)',
      maxPct: 15,
    },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-SNTL-2025-003',
    section: '4.1',
    kind: 'accessorials',
    label: 'Permitted extra charges',
    value: 'Lift-gate, inside delivery, re-delivery',
    detail:
      'Only lift-gate, inside delivery and re-delivery charges are payable, and only for services Pillar requested in advance.',
    quote:
      'Carrier shall not invoice any other charge, fee or surcharge unless Shipper has approved it in writing before the service was performed',
    confidence: 0.96,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-SNTL-2025-003',
    section: '4.2',
    kind: 'accessorials',
    label: 'Waiting time requires signed proof',
    value: 'Waiting time only with signed proof',
    detail:
      'Waiting-time charges are payable only after a 2-hour free period and only with proof signed by a Pillar facility representative.',
    quote:
      'Carrier shall not invoice detention without the signed proof, and Shipper is not obliged to pay any detention charge that is not supported by it',
    confidence: 0.98,
    enforced: true,
    rule: { kind: 'requires_proof', charge: 'waiting time' },
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-SNTL-2025-003',
    section: '5.2',
    kind: 'payment_terms',
    label: 'Payment terms',
    value: 'Net 15',
    detail: 'Pillar pays within 15 days after receiving a valid invoice.',
    quote:
      'Shipper shall pay each undisputed invoice within fifteen (15) days after receipt of a valid invoice',
    confidence: 0.99,
    enforced: true,
    rule: NET_15,
    risk: 'standard',
    riskNote: 'Shorter than the Net 45 on the Transcontinental agreement.',
  }),
  clause({
    contractId: 'MSA-SNTL-2025-003',
    section: '6.1',
    kind: 'sla',
    label: 'On-time performance',
    value: '95% on time, proof of delivery within 1 business day',
    detail:
      'Sentinel must pick up and deliver at least 95% of shipments on time and provide a signed proof of delivery within 1 business day.',
    quote:
      'Carrier shall pick up and deliver at least ninety-five percent (95%) of Shipments within the scheduled window',
    confidence: 0.96,
    enforced: false,
    risk: 'standard',
  }),
  clause({
    contractId: 'MSA-SNTL-2025-003',
    section: '6.2',
    kind: 'liability_cap',
    label: 'Cargo liability',
    value: 'Full value',
    detail:
      'Sentinel is liable for the full actual loss or damage to cargo in its possession, with $250,000 of cargo insurance per vehicle.',
    quote:
      'to the full extent provided by 49 U.S.C. Section 14706 (the Carmack Amendment)',
    confidence: 0.95,
    enforced: false,
    risk: 'favorable',
  }),
  clause({
    contractId: 'MSA-SNTL-2025-003',
    section: '7.1',
    kind: 'termination',
    label: 'Termination for convenience',
    value: 'Either party, 30 days notice',
    detail: 'Either party can terminate for convenience on 30 days of notice.',
    quote:
      'Either party may terminate this Agreement for convenience on thirty (30) days’ prior written notice',
    confidence: 0.97,
    enforced: false,
    rule: { kind: 'notice', days: 30 },
    risk: 'favorable',
  }),
];

const CLAUSES_BY_ID: Map<string, Clause> = new Map(
  CLAUSES.map((entry) => [entry.id, entry]),
);

export function getClause(id: string): Clause | undefined {
  return CLAUSES_BY_ID.get(id);
}

export function clausesForContract(contractId: string): Clause[] {
  return CLAUSES.filter((entry) => entry.contractId === contractId);
}

export function clausesByKind(kind: ClauseKind): Clause[] {
  return CLAUSES.filter((entry) => entry.kind === kind);
}
